import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Search as SearchIcon, Sparkles, Tag, Instagram, Mail } from "lucide-react";
import Layout from "@/components/Layout";
import ProductCard from "@/components/ProductCard";
import { api, resolveImage } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const CATEGORY_FALLBACKS = {
  disney: "https://images.unsplash.com/photo-1608889825205-eebdb9fc5806?auto=format&fit=crop&w=400&h=300",
  peluche: "https://images.unsplash.com/photo-1620149455234-b3013063ce43?auto=format&fit=crop&w=400&h=300",
  barbie: "https://images.unsplash.com/photo-1594736797933-d0501ba2fe65?auto=format&fit=crop&w=400&h=300",
  "paw-patrol": "https://images.unsplash.com/photo-1596461404969-9ae70f2830c1?auto=format&fit=crop&w=400&h=300",
  "giochi-preziosi": "https://images.unsplash.com/photo-1604531987601-cfdf61cddb91?auto=format&fit=crop&w=400&h=300",
  "action-figures": "https://images.unsplash.com/photo-1780726624500-8de106966b6f?auto=format&fit=crop&w=400&h=300",
  lego: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?auto=format&fit=crop&w=400&h=300",
  "veicoli-e-modellini": "https://images.unsplash.com/photo-1515281239448-2abe329fe5e5?auto=format&fit=crop&w=400&h=300",
  bambole: "https://images.unsplash.com/photo-1587654780291-39c9404d746b?auto=format&fit=crop&w=400&h=300",
  pokemon: "https://images.unsplash.com/photo-1613771404784-3a5686aa2be3?auto=format&fit=crop&w=400&h=300",
  "giochi-da-tavolo": "https://images.unsplash.com/photo-1610890716171-6b1bb98ffd09?auto=format&fit=crop&w=400&h=300",
};

function categoryFallback(slug) {
  const key = slug?.replace(/^(gn|uv)-/, "") || "";
  return CATEGORY_FALLBACKS[key] || "https://images.unsplash.com/photo-1570380888289-13cc37a74a0a?auto=format&fit=crop&w=400&h=300";
}

const TIKTOK_ICON = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
    <path d="M19.6 6.3a5.6 5.6 0 0 1-3.3-1.1V15a5.5 5.5 0 1 1-5.5-5.5c.3 0 .6 0 .9.1v2.7a2.8 2.8 0 1 0 2 2.7V2h2.6a5.5 5.5 0 0 0 3.3 4.3z"/>
  </svg>
);

export default function Home() {
  const [home, setHome] = useState(null);
  const [cats, setCats] = useState([]);
  const [featured, setFeatured] = useState([]);
  const [q, setQ] = useState("");
  const nav = useNavigate();

  useEffect(() => {
    (async () => {
      const [h, c, f] = await Promise.all([
        api.get("/home"),
        api.get("/categories"),
        api.get("/products", { params: { featured: true, limit: 8 } }),
      ]);
      setHome(h.data);
      setCats(c.data);
      setFeatured(f.data);
    })();
  }, []);

  const doSearch = (e) => {
    e.preventDefault();
    if (q.trim()) nav(`/ricerca?q=${encodeURIComponent(q.trim())}`);
  };

  const roots = cats.filter((c) => !c.parent_id && !c.hidden);
  const subs = cats.filter((c) => c.parent_id && !c.hidden);
  // Show a curated list of subcategories on the Home; if admin has selected specific slugs, use those
  const featuredSlugs = home?.featured_category_slugs || [];
  const homeSubs = featuredSlugs.length > 0
    ? featuredSlugs.map((s) => cats.find((c) => c.slug === s)).filter(Boolean)
    : subs.filter((c) => c.parent_id === roots.find((r) => r.slug === "giocattoli-nuovi")?.id).slice(0, 12);

  return (
    <Layout home={home}>
      {/* Hero with logo */}
      <section className="px-4 pt-8 pb-6 text-center">
        {home?.logo_url && (
          <img
            src={home.logo_url}
            alt="Spaccatempo Toys"
            className="mx-auto max-h-40 sm:max-h-52 w-auto object-contain"
            data-testid="home-logo"
          />
        )}
        <h1 className="mt-4 font-heading font-black text-2xl sm:text-3xl text-brand-blue">
          {home?.banner_title || "Spaccatempo – Toys & Collectibles"}
        </h1>
        <p className="mt-2 text-slate-600 text-sm sm:text-base max-w-xl mx-auto">
          {home?.banner_subtitle}
        </p>
        {home?.promo_message && (
          <div className="mt-3 inline-block bg-brand-yellow text-brand-blue font-bold text-xs px-3 py-1.5 rounded-full">
            {home.promo_message}
          </div>
        )}
        <form onSubmit={doSearch} className="mt-6 relative max-w-md mx-auto">
          <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" strokeWidth={2.5} />
          <Input
            data-testid="home-search-input"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Cerca giocattoli, marche, LEGO, Gormiti..."
            className="pl-11 h-12 rounded-full bg-white border-border shadow-sm"
          />
        </form>
      </section>

      {/* Root categories - big tiles */}
      <section className="px-4 mt-2">
        <div className="grid grid-cols-2 gap-3">
          {roots.map((c) => (
            <Link
              key={c.id}
              to={`/catalogo/${c.slug}`}
              data-testid={`root-tile-${c.slug}`}
              className="relative overflow-hidden rounded-2xl bg-brand-blue text-white h-32 sm:h-40 group hover:-translate-y-0.5 transition"
            >
              <div className="absolute inset-0 bg-gradient-to-br from-brand-blue to-blue-900 opacity-95" />
              <div className="relative h-full flex flex-col justify-end p-4">
                <div className="text-xs uppercase tracking-widest text-brand-yellow font-bold">Scopri</div>
                <div className="font-heading font-black text-lg sm:text-2xl leading-tight">{c.name}</div>
              </div>
              <div className="absolute -right-8 -top-8 w-24 h-24 rounded-full bg-brand-yellow/25 blur-2xl" />
            </Link>
          ))}
        </div>
      </section>

      {/* Welcome block */}
      <section className="px-4 mt-10">
        <div className="bg-white border border-border rounded-3xl p-6 sm:p-8 text-center">
          <h2 className="font-heading font-black text-2xl sm:text-3xl text-brand-blue">
            {home?.welcome_title || "Benvenuti da Spaccatempo"}
          </h2>
          <p className="mt-3 text-slate-600 max-w-2xl mx-auto text-sm sm:text-base leading-relaxed">
            {home?.welcome_text}
          </p>
          {home?.hero_images?.length > 0 && (
            <div className="mt-5 grid grid-cols-2 sm:grid-cols-4 gap-2">
              {home.hero_images.slice(0, 4).map((img, i) => (
                <img key={i} src={resolveImage(img)} alt="" className="w-full aspect-[4/3] object-cover rounded-xl" />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Subcategories grid with images */}
      {homeSubs.length > 0 && (
        <section className="px-4 mt-10">
          <div className="flex items-baseline justify-between mb-4">
            <h2 className="font-heading font-black text-xl sm:text-2xl text-brand-blue">Sfoglia per categoria</h2>
            <Link to="/catalogo" className="text-xs font-bold text-brand-blue" data-testid="see-all-cats">Tutte →</Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
            {homeSubs.map((c) => {
              const img = c.image_url ? resolveImage(c.image_url) : categoryFallback(c.slug);
              return (
                <Link
                  key={c.id}
                  to={`/catalogo/${c.slug}`}
                  data-testid={`cat-tile-${c.slug}`}
                  className="group relative aspect-square overflow-hidden rounded-2xl bg-slate-100 border border-border hover:border-brand-blue transition"
                >
                  <img src={img} alt={c.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/10 to-transparent" />
                  <div className="absolute inset-x-0 bottom-0 p-3">
                    <div className="text-white font-heading font-black text-sm sm:text-base leading-tight">{c.name}</div>
                  </div>
                </Link>
              );
            })}
          </div>
        </section>
      )}

      {/* Featured products */}
      {featured.length > 0 && (
        <section className="mt-10" data-testid="row-featured">
          <div className="px-4 flex items-baseline justify-between mb-3">
            <h2 className="font-heading font-black text-xl text-brand-blue flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-yellow" /> In Evidenza
            </h2>
          </div>
          <div className="scroll-row flex gap-3 overflow-x-auto snap-x snap-mandatory px-4 pb-2">
            {featured.map((p) => (
              <div key={p.id} className="min-w-[160px] max-w-[180px] snap-start">
                <ProductCard product={p} />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* eBay + Vinted side-by-side */}
      {(home?.ebay_enabled || home?.vinted_enabled) && (
        <section className="px-4 mt-10">
          <div className="grid grid-cols-2 gap-3">
            {home?.ebay_enabled && (
              <a
                href={home.ebay_url}
                target="_blank"
                rel="noreferrer"
                data-testid="home-ebay-link"
                className="bg-white border border-border rounded-2xl p-4 hover:border-brand-blue transition"
              >
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Marketplace</div>
                <div className="font-heading font-black text-brand-blue text-lg leading-tight">eBay</div>
                <p className="mt-2 text-xs text-slate-600">{home.ebay_label}</p>
                <div className="mt-3 text-xs font-bold text-brand-blue">Visita eBay →</div>
              </a>
            )}
            {home?.vinted_enabled && (
              <a
                href={home.vinted_url}
                target="_blank"
                rel="noreferrer"
                data-testid="home-vinted-link"
                className="bg-white border border-border rounded-2xl p-4 hover:border-brand-blue transition"
              >
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold mb-1">Community</div>
                <div className="font-heading font-black text-brand-blue text-lg leading-tight">Vinted</div>
                <p className="mt-2 text-xs text-slate-600">{home.vinted_label}</p>
                <div className="mt-3 text-xs font-bold text-brand-blue">Vai su Vinted →</div>
              </a>
            )}
          </div>
        </section>
      )}

      {/* Newsletter */}
      {home?.newsletter_enabled && (
        <section className="px-4 mt-10">
          <div className="bg-brand-blue rounded-3xl p-6 sm:p-8 text-center text-white">
            <h2 className="font-heading font-black text-xl sm:text-2xl">Seguici nel mondo Spaccatempo</h2>
            <p className="mt-2 text-white/85 text-sm">{home.newsletter_text}</p>
            <form
              onSubmit={(e) => { e.preventDefault(); alert("Grazie! Ti terremo aggiornato."); }}
              className="mt-5 flex flex-col sm:flex-row gap-2 max-w-md mx-auto"
            >
              <Input
                type="email"
                data-testid="newsletter-email"
                placeholder="La tua email"
                className="h-11 rounded-full bg-white text-slate-900 border-0"
                required
              />
              <Button type="submit" data-testid="newsletter-submit-btn" className="h-11 rounded-full bg-brand-yellow hover:bg-brand-yellow-dark text-brand-blue font-bold">
                Invia ora
              </Button>
            </form>
          </div>
        </section>
      )}

      {/* Contacts / Social */}
      <section className="px-4 mt-10">
        <div className="text-center">
          <h3 className="font-heading font-black text-lg text-brand-blue">Contatti</h3>
          <p className="mt-1 text-sm text-slate-600">Scrivici per dubbi, consigli o suggerimenti</p>
          <div className="mt-3 flex items-center justify-center gap-3">
            {home?.instagram_url && (
              <a href={home.instagram_url} target="_blank" rel="noreferrer" data-testid="social-ig" className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-brand-blue-dark transition">
                <Instagram className="w-5 h-5" />
              </a>
            )}
            {home?.tiktok_url && (
              <a href={home.tiktok_url} target="_blank" rel="noreferrer" data-testid="social-tt" className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-brand-blue-dark transition">
                <TIKTOK_ICON className="w-5 h-5" />
              </a>
            )}
            {home?.contact_email && (
              <a href={`mailto:${home.contact_email}`} data-testid="social-email" className="w-10 h-10 rounded-full bg-brand-blue text-white flex items-center justify-center hover:bg-brand-blue-dark transition">
                <Mail className="w-5 h-5" />
              </a>
            )}
            {home?.whatsapp_url && (
              <a href={home.whatsapp_url} target="_blank" rel="noreferrer" data-testid="social-wa" className="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:bg-emerald-600 transition">
                <svg viewBox="0 0 24 24" fill="currentColor" className="w-5 h-5"><path d="M20.5 3.5A11.9 11.9 0 0 0 12 0C5.4 0 0 5.4 0 12c0 2.1.6 4.2 1.6 6L0 24l6.2-1.6A11.9 11.9 0 0 0 12 24c6.6 0 12-5.4 12-12 0-3.2-1.2-6.2-3.5-8.5zM12 22c-1.9 0-3.7-.5-5.3-1.4l-.4-.2-3.7 1 1-3.6-.2-.4A9.9 9.9 0 0 1 2 12C2 6.5 6.5 2 12 2s10 4.5 10 10-4.5 10-10 10zm5.5-7.5c-.3-.1-1.8-.9-2-1-.3-.1-.5-.1-.7.2-.2.3-.8 1-1 1.2-.2.2-.4.2-.6.1-.4-.2-1.5-.6-2.9-1.8-1.1-1-1.8-2.2-2-2.5-.2-.4 0-.5.2-.7.2-.1.4-.3.5-.4.2-.2.2-.3.3-.5.1-.2.1-.4 0-.5 0-.1-.7-1.7-1-2.3-.2-.6-.5-.5-.7-.5h-.6c-.2 0-.5.1-.8.4-.3.3-1 1-1 2.5s1.1 2.9 1.2 3.1c.1.2 2.2 3.4 5.4 4.7.8.3 1.4.5 1.9.7.8.3 1.5.2 2.1.1.6-.1 1.8-.7 2-1.4.3-.7.3-1.3.2-1.4l-.5-.3z"/></svg>
              </a>
            )}
          </div>
          {home?.contact_email && (
            <p className="mt-3 text-xs text-slate-500">{home.contact_email}</p>
          )}
        </div>
      </section>

      {/* Blue footer */}
      <footer className="mt-10 bg-brand-blue text-white/90 py-6 text-center text-xs" data-testid="home-footer">
        <p>{home?.footer_message}</p>
        <p className="mt-1 opacity-70">
          <Link to="/privacy" className="hover:text-brand-yellow underline">Privacy Policy</Link>
        </p>
      </footer>
    </Layout>
  );
}
